import React, { useEffect, useState } from 'react';

// Ankara koordinatları — anahtarsız Open-Meteo API kullanılır.
const ANKARA = { lat: 39.93, lon: 32.86, label: 'Ankara' };

const WEATHER_API_URL =
  `https://api.open-meteo.com/v1/forecast?latitude=${ANKARA.lat}&longitude=${ANKARA.lon}` +
  '&current=temperature_2m,weather_code,is_day&daily=temperature_2m_max,temperature_2m_min&timezone=Europe%2FIstanbul';

// WMO hava durumu kodlarını Türkçe etiket + sahne eşlemesi.
const WEATHER_CODES = {
  0: { label: 'Açık', scene: 'clear' },
  1: { label: 'Az bulutlu', scene: 'partly' },
  2: { label: 'Parçalı bulutlu', scene: 'partly' },
  3: { label: 'Bulutlu', scene: 'clouds' },
  45: { label: 'Sisli', scene: 'fog' },
  48: { label: 'Sisli', scene: 'fog' },
  51: { label: 'Çiseleme', scene: 'rain' },
  53: { label: 'Çiseleme', scene: 'rain' },
  55: { label: 'Çiseleme', scene: 'rain' },
  61: { label: 'Hafif yağmur', scene: 'rain' },
  63: { label: 'Yağmurlu', scene: 'rain' },
  65: { label: 'Kuvvetli yağmur', scene: 'rain' },
  71: { label: 'Hafif kar', scene: 'snow' },
  73: { label: 'Karlı', scene: 'snow' },
  75: { label: 'Yoğun kar', scene: 'snow' },
  77: { label: 'Kar taneli', scene: 'snow' },
  80: { label: 'Sağanak', scene: 'rain' },
  81: { label: 'Sağanak', scene: 'rain' },
  82: { label: 'Kuvvetli sağanak', scene: 'rain' },
  85: { label: 'Kar sağanağı', scene: 'snow' },
  86: { label: 'Kar sağanağı', scene: 'snow' },
  95: { label: 'Gök gürültülü', scene: 'storm' },
  96: { label: 'Gök gürültülü', scene: 'storm' },
  99: { label: 'Gök gürültülü', scene: 'storm' }
};

const describeWeather = (code) => WEATHER_CODES[code] || { label: 'Bilinmiyor', scene: 'clouds' };

// Yeniden kullanılan SVG parçaları.
const SunCore = ({ cx = 64, cy = 58, r = 22 }) => (
  <>
    <circle cx={cx} cy={cy} r={r + 10} fill="url(#kw-sun-glow)" />
    <g className="kw-rays" style={{ transformOrigin: `${cx}px ${cy}px` }}>
      {Array.from({ length: 8 }).map((_, i) => (
        <rect
          key={i}
          x={cx - 1.6}
          y={cy - r - 16}
          width="3.2"
          height="11"
          rx="1.6"
          fill="#ffd166"
          transform={`rotate(${i * 45} ${cx} ${cy})`}
        />
      ))}
    </g>
    <circle cx={cx} cy={cy} r={r} fill="url(#kw-sun)" />
  </>
);

const Cloud = ({ x = 0, y = 0, scale = 1, light = true, className = '' }) => (
  <g transform={`translate(${x} ${y}) scale(${scale})`} className={className}>
    <ellipse cx="40" cy="40" rx="34" ry="20" fill={light ? 'url(#kw-cloud)' : 'url(#kw-cloud-dark)'} />
    <circle cx="26" cy="34" r="16" fill={light ? 'url(#kw-cloud)' : 'url(#kw-cloud-dark)'} />
    <circle cx="48" cy="28" r="20" fill={light ? 'url(#kw-cloud)' : 'url(#kw-cloud-dark)'} />
  </g>
);

const Moon = ({ cx = 70, cy = 52, r = 22 }) => (
  <g>
    <circle cx={cx} cy={cy} r={r + 8} fill="url(#kw-sun-glow)" opacity="0.5" />
    <path
      d={`M ${cx + 6} ${cy - r} a ${r} ${r} 0 1 0 0 ${2 * r} a ${r - 8} ${r - 8} 0 1 1 0 -${2 * r} Z`}
      fill="url(#kw-moon)"
    />
  </g>
);

const WeatherArt = ({ scene, isDay }) => (
  <svg className="kw-art" viewBox="0 0 128 110" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <defs>
      <radialGradient id="kw-sun" cx="40%" cy="35%" r="70%">
        <stop offset="0%" stopColor="#fff3c4" />
        <stop offset="55%" stopColor="#ffd166" />
        <stop offset="100%" stopColor="#ff9c04" />
      </radialGradient>
      <radialGradient id="kw-sun-glow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="rgba(255,209,102,0.7)" />
        <stop offset="100%" stopColor="rgba(255,209,102,0)" />
      </radialGradient>
      <radialGradient id="kw-moon" cx="38%" cy="35%" r="75%">
        <stop offset="0%" stopColor="#ffffff" />
        <stop offset="100%" stopColor="#cdd9e5" />
      </radialGradient>
      <linearGradient id="kw-cloud" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#ffffff" />
        <stop offset="100%" stopColor="#dbe6ee" />
      </linearGradient>
      <linearGradient id="kw-cloud-dark" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#c4d0da" />
        <stop offset="100%" stopColor="#94a6b4" />
      </linearGradient>
    </defs>

    {scene === 'clear' && (isDay ? <SunCore /> : <Moon />)}

    {scene === 'partly' && (
      <>
        {isDay ? <SunCore cx={48} cy={44} r={17} /> : <Moon cx={50} cy={42} r={16} />}
        <Cloud x={48} y={42} scale={0.85} />
      </>
    )}

    {scene === 'clouds' && (
      <>
        <Cloud x={12} y={20} scale={1.05} className="kw-cloud-back" />
        <Cloud x={44} y={44} scale={0.7} light={false} />
      </>
    )}

    {scene === 'fog' && (
      <>
        <Cloud x={24} y={24} scale={0.95} />
        <g className="kw-fog-lines" stroke="#e3edf3" strokeWidth="4" strokeLinecap="round">
          <line x1="30" y1="84" x2="98" y2="84" />
          <line x1="22" y1="94" x2="90" y2="94" />
          <line x1="38" y1="104" x2="106" y2="104" />
        </g>
      </>
    )}

    {scene === 'rain' && (
      <>
        <Cloud x={24} y={20} scale={1} light={false} />
        <g className="kw-rain-group" stroke="#9fd3ff" strokeWidth="3.4" strokeLinecap="round">
          {[36, 54, 72, 90].map((x, i) => (
            <line key={i} x1={x} y1="74" x2={x - 6} y2="92" style={{ animationDelay: `${i * 0.15}s` }} />
          ))}
        </g>
      </>
    )}

    {scene === 'snow' && (
      <>
        <Cloud x={24} y={20} scale={1} />
        <g className="kw-snow-group" fill="#ffffff">
          {[36, 56, 76, 92].map((x, i) => (
            <circle key={i} cx={x} cy="80" r="3.4" style={{ animationDelay: `${i * 0.35}s` }} />
          ))}
        </g>
      </>
    )}

    {scene === 'storm' && (
      <>
        <Cloud x={24} y={18} scale={1.05} light={false} />
        <polygon className="kw-bolt-art" points="62,66 52,86 60,86 54,104 78,80 66,80 74,66" fill="#ffd166" />
      </>
    )}
  </svg>
);

const WeatherCard = ({ variant = 'default' }) => {
  const [weather, setWeather] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | error

  useEffect(() => {
    let active = true;

    const fetchWeather = async () => {
      try {
        setStatus('loading');
        const response = await fetch(WEATHER_API_URL);
        if (!response.ok) {
          throw new Error(`Hava durumu alınamadı (${response.status})`);
        }
        const data = await response.json();
        if (!active) {
          return;
        }
        setWeather({
          temp: Math.round(data.current?.temperature_2m),
          code: data.current?.weather_code,
          isDay: data.current?.is_day !== 0,
          max: Math.round(data.daily?.temperature_2m_max?.[0]),
          min: Math.round(data.daily?.temperature_2m_min?.[0])
        });
        setStatus('ready');
      } catch (error) {
        console.error('Hava durumu yüklenemedi:', error);
        if (active) {
          setStatus('error');
        }
      }
    };

    fetchWeather();
    return () => {
      active = false;
    };
  }, []);

  const condition = weather ? describeWeather(weather.code) : null;
  const sceneClass = condition ? `kw-${condition.scene}` : '';
  const nightClass = weather && !weather.isDay ? ' is-night' : '';

  return (
    <section className={`kolin-card kolin-col-hava kolin-weather kolin-weather-${variant} ${sceneClass}${nightClass}`}>
      <div className="kolin-weather-head">
        <i className="bx bxs-map" aria-hidden="true"></i>
        <span>{ANKARA.label}</span>
      </div>

      {status === 'loading' && <p className="kolin-weather-state">Yükleniyor…</p>}
      {status === 'error' && <p className="kolin-weather-state">Hava durumu şu an alınamıyor.</p>}

      {status === 'ready' && weather && (
        <>
          <div className="kolin-weather-art-wrap">
            <WeatherArt scene={condition.scene} isDay={weather.isDay} />
          </div>
          <div className="kolin-weather-body">
            <div className="kolin-weather-temp">
              {Number.isFinite(weather.temp) ? `${weather.temp}°` : '—'}
            </div>
            <p className="kolin-weather-label">{condition.label}</p>
            {Number.isFinite(weather.max) && Number.isFinite(weather.min) && (
              <p className="kolin-weather-range">
                <span><i className="bx bx-up-arrow-alt" aria-hidden="true"></i>{weather.max}°</span>
                <span><i className="bx bx-down-arrow-alt" aria-hidden="true"></i>{weather.min}°</span>
              </p>
            )}
          </div>
        </>
      )}
    </section>
  );
};

export default WeatherCard;
